import { t } from "@lingui/macro";
import { SparkleIcon, SpinnerIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import type { ReactNode } from "react";

/**
 * SummaryForm — single rich-text block pattern (Apply Edge builder v2).
 *
 * Presentational. The editor itself is NOT built here — the parent passes it via
 * `richTextSlot`. This component frames it with the calm card, a live word/char
 * count, and an AI writing-assist affordance (`onAiAssist`).
 */
export type SummaryFormProps = {
  richTextSlot: ReactNode;
  wordCount?: number;
  charCount?: number;
  onAiAssist?: () => void;
  aiAssisting?: boolean;
  /** Optional soft target shown next to the count, e.g. "Aim for 40–60 words". */
  hint?: string;
};

export const SummaryForm = ({
  richTextSlot,
  wordCount,
  charCount,
  onAiAssist,
  aiAssisting = false,
  hint,
}: SummaryFormProps) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-xl border border-border bg-background p-1 focus-within:ring-2 focus-within:ring-primary">
        {richTextSlot}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="font-mono text-[10px] text-muted-foreground">
          {wordCount === undefined ? null : t`${wordCount} words`}
          {wordCount === undefined || charCount === undefined ? null : " · "}
          {charCount === undefined ? null : t`${charCount} characters`}
          {hint === undefined ? null : (
            <span className="ml-2 normal-case tracking-normal opacity-80">{hint}</span>
          )}
        </p>

        {onAiAssist ? (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-primary hover:text-primary"
            disabled={aiAssisting}
            aria-label={t`Write with AI`}
            onClick={onAiAssist}
          >
            {aiAssisting ? (
              <SpinnerIcon size={14} className="animate-spin" />
            ) : (
              <SparkleIcon size={14} />
            )}
            {aiAssisting ? t`Writing…` : t`Write with AI`}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
